import {useEffect, useState} from 'react'
import {useQueryClient} from '@tanstack/react-query'
// /max, not the default "min" metadata — min only checks number LENGTH (see IdentityCompletionFlow).
import {parsePhoneNumberFromString} from 'libphonenumber-js/max'
import OtpInput, {formatOtpTimer} from '@/customComponents/uiComponents/global/OtpInput.jsx'
import FlagSelectWithNumber from '@/customComponents/uiComponents/global/FlagSelectWithNumber.jsx'
import SocialLoginButtons from '@/customComponents/authComponents/SocialLoginButtons.jsx'
import {Loader} from '@/customComponents/uiComponents/Loader.jsx'
import {userService} from '@/services/userService'
import {useAuth} from '@/context/AuthContext'
import {BOOKING_CONSTRAINTS} from '@/constants/booking'

/**
 * COMPLETE A PROFILE THE BOOKING GATE REFUSED — the sign-up screen's layout, all the missing
 * fields on ONE screen. (IdentityCompletionFlow asks for them one at a time; it is still what
 * the other confirm pages use.)
 *
 *   • Google / Apple under "Continue with" — shown only while a NAME or an EMAIL is missing.
 *     They PRE-FILL first name, last name and email from the provider; they do not sign in
 *     (a passenger who is already signed in must not be swapped into the Google account).
 *   • Only the fields the server listed are rendered: first + last name, email, phone.
 *   • Continue saves the name, then walks the contacts one at a time — email, then phone:
 *       add → send code → verify → set default
 *     ⚠️ A pre-filled email is NOT treated as verified. Google's claim is read client-side
 *     from an unverified token, so the six-digit code still proves the address.
 *
 * Same API calls, in the same order, as IdentityCompletionFlow — see its note on why "set
 * default" is what actually clears the gate.
 *
 * @param {string[]} missing      'name' | 'email' | 'phone' — what the server still wants
 * @param {Function} onCompleted  every listed field is done
 * @param {Function} onTerms      the terms / privacy links
 */
const EMPTY_OTP = () => Array(6).fill('')
const INPUT = 'rounded-full w-full px-5 py-3.5 max-md:py-2.5 max-md:text-sm outline-none bg-transparent border border-(--bg-dark-grey)'
const INPUT_REQUIRED = 'border-(--text-red)!'
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const CONTACT_ORDER = ['email', 'phone']

const firstFieldError = (raw, fallback) => {
    const first = raw?.errors && Object.values(raw.errors)[0]?.[0]
    return first || fallback
}
// `email_already_in_use` / `phone_already_in_use` = the contact belongs to ANOTHER account. That is
// not "already added here" — carrying on to the code step only ends in "Email not found.".
const takenByAnother = (raw) => /in_use/i.test(raw?.key || '')
const alreadyThere = (message) => /already|exist/i.test(message || '')

export default function CompleteProfileForm({missing, onCompleted, onTerms}) {
    const queryClient = useQueryClient()
    const {login} = useAuth()
    // Frozen at mount — the account refetch that follows each saved field would otherwise
    // reshuffle the form under the person's hands.
    const [wanted] = useState(() => [...(missing || [])])
    const needName = wanted.includes('name')
    const needEmail = wanted.includes('email')
    const needPhone = wanted.includes('phone') || (!needName && !needEmail)
    const contacts = CONTACT_ORDER.filter((field) => (field === 'email' ? needEmail : needPhone))
    const showSocial = needName || needEmail

    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [otpField, setOtpField] = useState(null)   // null = the form; 'email' | 'phone' = its code screen
    const [otp, setOtp] = useState(EMPTY_OTP)
    const [resendTimer, setResendTimer] = useState(0)
    const [submitted, setSubmitted] = useState(false) // outline what is still empty
    const [nameSaved, setNameSaved] = useState(false)
    const [error, setError] = useState('')
    const [busy, setBusy] = useState(false)
    const [socialBusy, setSocialBusy] = useState(false) // a Google / Apple popup is open — lock the fields
    const [autoAdvance, setAutoAdvance] = useState(false) // a provider just pre-filled — move on if it filled everything

    useEffect(() => {
        if (resendTimer <= 0) return undefined
        const timer = setInterval(() => setResendTimer((prev) => Math.max(prev - 1, 0)), 1000)
        return () => clearInterval(timer)
    }, [resendTimer])

    const phoneParts = () => {
        const parsed = parsePhoneNumberFromString(phone || '')
        return parsed && parsed.isValid() ? {number: parsed.number, country: parsed.country?.toLowerCase() || null} : null
    }
    const targetOf = (field) => (field === 'email' ? email.trim() : (phoneParts()?.number || phone))

    const finish = () => {
        queryClient.invalidateQueries({queryKey: ['user', 'account']})
        onCompleted?.()
    }

    // Google / Apple → pre-fill only what is wanted and still empty, never overwrite typing.
    const handleProfile = ({firstName: f, lastName: l, email: m}) => {
        if (needName) {
            if (f) setFirstName((cur) => cur || f)
            if (l) setLastName((cur) => cur || l)
        }
        if (needEmail && m) setEmail((cur) => cur || m)
        setError('')
        setAutoAdvance(true)
    }

    // add → send the code, then show the code screen for that contact.
    const sendCode = async (field) => {
        setError('')
        const parts = field === 'phone' ? phoneParts() : null
        const target = field === 'email' ? email.trim() : parts?.number
        setBusy(true)
        try {
            const added = field === 'email'
                ? await userService.addEmail(target)
                : await userService.addPhone(target, parts.country)
            if (!added.success && takenByAnother(added.raw)) {
                setError(`This ${field === 'email' ? 'email address' : 'phone number'} is already linked to another account. Please use a different one.`)
                return false
            }
            if (!added.success && !alreadyThere(firstFieldError(added.raw, added.error))) {
                setError(firstFieldError(added.raw, added.error) || `Failed to add ${field}`)
                return false
            }
            const sent = field === 'email'
                ? await userService.requestEmailOtp(target)
                : await userService.requestPhoneOtp(target)
            if (!sent.success) {
                setError(firstFieldError(sent.raw, sent.error) || 'Failed to send code')
                return false
            }
            setOtp(EMPTY_OTP())
            setOtpField(field)
            setResendTimer(BOOKING_CONSTRAINTS.OTP_RESEND_TIMER_SECONDS)
            return true
        } catch (err) {
            setError(err?.message || 'Unexpected error while sending code')
            return false
        } finally {
            setBusy(false)
        }
    }

    // The form's Continue: validate everything, save the name, then start on the first contact.
    const submitForm = async () => {
        setError('')
        setSubmitted(true)
        if (needName && (!firstName.trim() || !lastName.trim())) return setError('First and last name are required')
        if (needEmail && !EMAIL_RE.test(email.trim())) return setError('Please enter a valid email address')
        if (needPhone && !phoneParts()) return setError('Please enter a valid phone number')

        if (needName && !nameSaved) {
            setBusy(true)
            const result = await userService.updateAccount({first_name: firstName.trim(), last_name: lastName.trim()})
            setBusy(false)
            if (!result.success) return setError(firstFieldError(result.raw, result.error) || 'Could not save your name.')
            if (result.token) login(result.token)
            setNameSaved(true)
        }
        if (!contacts.length) return finish()
        await sendCode(contacts[0])
    }

    // After a Google / Apple pre-fill (state is committed by now): if the provider supplied every
    // field the form asks for, go straight to the next step instead of making the person press
    // Continue. Anything still missing (e.g. a phone number) leaves them on the form.
    useEffect(() => {
        if (!autoAdvance) return
        setAutoAdvance(false)
        if (busy || otpField) return
        const complete = (!needName || (firstName.trim() && lastName.trim()))
            && (!needEmail || EMAIL_RE.test(email.trim()))
            && (!needPhone || phoneParts())
        if (complete) submitForm()
    }, [autoAdvance]) // eslint-disable-line react-hooks/exhaustive-deps

    const verify = async () => {
        setError('')
        const code = otp.join('')
        if (code.length < 6) return setError('Please enter a valid 6-digit code')
        setBusy(true)
        try {
            const target = targetOf(otpField)
            const verified = otpField === 'email'
                ? await userService.verifyEmailOtp(target, code)
                : await userService.verifyPhoneOtp(target, code)
            if (!verified.success) {
                setError(firstFieldError(verified.raw, verified.error) || 'Invalid code')
                setOtp(EMPTY_OTP())
                return
            }
            const made = otpField === 'email'
                ? await userService.setDefaultEmail(target)
                : await userService.setDefaultPhone(target)
            if (!made.success) return setError(firstFieldError(made.raw, made.error) || 'Failed to set as default')
        } catch (err) {
            return setError(err?.message || 'Unexpected error while verifying code')
        } finally {
            setBusy(false)
        }
        const next = contacts[contacts.indexOf(otpField) + 1]
        if (next) {
            await sendCode(next)
        } else {
            finish()
        }
    }

    // ── code screen ─────────────────────────────────────────────────────────────
    if (otpField) {
        return (
            <div className="text-center">
                <h4 className="font-semibold mb-3! max-md:text-xl!">Verify your {otpField === 'email' ? 'email' : 'phone'}</h4>
                <p className="text-(--text-secondary)">
                    We sent a code to <span className="font-bold text-(--text-primary)">{targetOf(otpField)}</span>
                </p>
                <OtpInput value={otp} onChange={(next) => { setOtp(next); setError('') }} autoFocus className="my-4"/>
                <div className="flex items-center justify-center gap-3">
                    <button type="button" onClick={() => sendCode(otpField)} disabled={resendTimer > 0 || busy}
                            className={`text-sm font-medium border-0 bg-transparent p-0 ${
                                resendTimer > 0 || busy ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer text-(--text-primary) hover:opacity-80'
                            }`}>
                        Resend code
                    </button>
                    {resendTimer > 0 && <span className="text-sm text-(--text-secondary)">{formatOtpTimer(resendTimer)}</span>}
                </div>
                <button type="button" onClick={() => { setOtpField(null); setError('') }} disabled={busy}
                        className="mt-3 text-sm underline border-0 bg-transparent p-0 cursor-pointer text-(--text-primary)">
                    Change {otpField === 'email' ? 'email' : 'phone number'}
                </button>

                {error && <p className="text-red-500 text-sm mt-3 mb-0!">{error}</p>}

                <button type="button" onClick={verify} disabled={busy} className="hopn-add-passenger-button mt-5">
                    {busy ? <Loader color="#fff"/> : 'Verify'}
                </button>
            </div>
        )
    }

    // ── the form ────────────────────────────────────────────────────────────────
    return (
        <div className="text-center">
            {showSocial ? (
                <>
                    <h4 className="font-semibold mb-6! max-md:text-xl!">Continue with</h4>
                    <SocialLoginButtons large onProfile={handleProfile} onBusyChange={setSocialBusy} disabled={busy}/>
                    {/* The dashed "or" rule, as on the sign-up screen. */}
                    <div className="flex items-center justify-center gap-3 my-6 text-sm text-(--text-secondary)">
                        <span className="flex-1 max-w-[110px] border-t border-dashed border-[#E6E4E4]"/>
                        or
                        <span className="flex-1 max-w-[110px] border-t border-dashed border-[#E6E4E4]"/>
                    </div>
                </>
            ) : (
                // Phone only — no heading, no providers (they never supply a number).
                <p className="text-(--text-secondary) mb-5!">
                    We need your phone number before you can book — your driver uses it to reach you about the ride.
                </p>
            )}

            <div className="flex flex-col gap-4 text-left">
                {needName && (
                    <div className="flex gap-4">
                        <input type="text" placeholder="First name" value={firstName} disabled={socialBusy}
                               className={`${INPUT} min-w-0 ${submitted && !firstName.trim() ? INPUT_REQUIRED : ''}`}
                               onChange={(ev) => { setFirstName(ev.target.value); setError('') }}/>
                        <input type="text" placeholder="Last name" value={lastName} disabled={socialBusy}
                               className={`${INPUT} min-w-0 ${submitted && !lastName.trim() ? INPUT_REQUIRED : ''}`}
                               onChange={(ev) => { setLastName(ev.target.value); setError('') }}/>
                    </div>
                )}
                {needEmail && (
                    <input type="email" placeholder="Email address" value={email} disabled={socialBusy}
                           className={`${INPUT} ${submitted && !EMAIL_RE.test(email.trim()) ? INPUT_REQUIRED : ''}`}
                           onChange={(ev) => { setEmail(ev.target.value); setError('') }}/>
                )}
                {needPhone && (
                    <FlagSelectWithNumber value={phone} readOnly={socialBusy} onChange={(value) => { setPhone(value || ''); setError('') }}/>
                )}
            </div>

            {error && <p className="text-red-500 text-sm mt-3 mb-0!">{error}</p>}

            <button type="button" onClick={submitForm} disabled={busy || socialBusy} className="hopn-add-passenger-button mt-6">
                {busy ? <Loader color="#fff"/> : 'Continue'}
            </button>

            <p className="text-sm text-center mt-5 mb-0!">
                By continuing you accept the<br/>
                <button type="button" onClick={onTerms} className="underline border-0 bg-transparent p-0 cursor-pointer text-(--text-primary)">Term of service</button>
                {' '}and{' '}
                <button type="button" onClick={onTerms} className="underline border-0 bg-transparent p-0 cursor-pointer text-(--text-primary)">Privacy Policy</button>
            </p>
        </div>
    )
}
